import type { ReactElement } from 'react';
import { StudioBox, type StudioNodeRendererProps } from './box';

/**
 * Structural nodes (Slide, Stack, Row, Split, Grid, Cluster, Overlay, Bleed,
 * Inset, Rail, Hero, CanvasRegion) "just position children" (outcome #1) —
 * every child already carries its own absolute box (see
 * `ResolvedSceneNode.box`'s doc comment), so the structural node itself
 * contributes no visual UNLESS the agent flagged `props.background`, in
 * which case it paints a flat tint from `props.tone`/`props.gradient` so a
 * background slide/overlay isn't just invisible children floating on the
 * card's own surface colour.
 */
function StructuralNode({ node }: StudioNodeRendererProps): ReactElement | null {
  if (!node.props?.background) return null;
  return <StudioBox node={node} className="tpl-studio-structural-bg" />;
}

export const SlideNode = StructuralNode;
export const StackNode = StructuralNode;
export const RowNode = StructuralNode;
export const SplitNode = StructuralNode;
export const GridNode = StructuralNode;
export const ClusterNode = StructuralNode;
export const OverlayNode = StructuralNode;
export const BleedNode = StructuralNode;
export const InsetNode = StructuralNode;
export const RailNode = StructuralNode;
export const HeroNode = StructuralNode;
export const CanvasRegionNode = StructuralNode;

/**
 * Global chrome (Footer, PageNumber, AgencyClientLockup) is rendered by
 * `StudioPage`'s `Frame` mount, never by the agent (types.ts: "listed for
 * completeness of the closed set"). If the resolver ever emits one anyway,
 * silently no-op — it's a legal type, not an unsupported one, so no
 * console.warn.
 */
function GlobalChromeNode(): null {
  return null;
}

export const FooterNode = GlobalChromeNode;
export const PageNumberNode = GlobalChromeNode;
export const AgencyClientLockupNode = GlobalChromeNode;
